import React, { useCallback, useEffect, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ContactApi } from "src/services/Api/Organization/types";
import { api } from "src/services/Api/api";
import { AddressApi } from "src/services/Api/types";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import ImportCsv from "go-component/components/ImportCsv/ImportCsv";
import { camelToSnakeCase } from "go-component/utils/componentUtils";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldEntityApi, CustomFieldTemplateApi } from "go-segment/services/types";

const getTranslationsConfig = (t: TFunction) => {
	return {
		export_csv: t("modules.contact.action.export_csv.title"),
		resource_name: t("modules.contact.field.resource_name.title"),
		constraints: {
			cannot_be_empty: t("constraints.cannot_be_empty"),
			csv_corrupted: t("constraints.csv_corrupted"),
			duplicate_name: t("constraints.duplicate_name"),
			duplicate_tax_id_no: t("constraints.duplicate_tax_id_no"),
			invalid_custom_field_option: t("constraints.invalid_custom_field_option"),
			invalid_custom_field_value: t("constraints.invalid_custom_field_value"),
			invalid_email: t("constraints.invalid_email"),
			invalid_value: t("constraints.invalid_value"),
			max_length: t("constraints.max_length"),
		},
		database_column_name: {
			uniqueId: t("enums.resource_type.contacts.uniqueId"),
			name: t("enums.resource_type.contacts.name"),
			taxIdNo: t("enums.resource_type.contacts.taxIdNo"),
			addressStreet: t("enums.resource_type.contacts.addressStreet"),
			addressBuildingNr: t("enums.resource_type.contacts.addressBuildingNr"),
			addressFlatNr: t("enums.resource_type.contacts.addressFlatNr"),
			addressCity: t("enums.resource_type.contacts.addressCity"),
			addressCountry: t("enums.resource_type.contacts.addressCountry"),
			addressZipCode: t("enums.resource_type.contacts.addressZipCode"),
			contactPhoneNr: t("enums.resource_type.contacts.contactPhoneNr"),
			contactEmail: t("enums.resource_type.contacts.contactEmail"),
			description: t("enums.resource_type.contacts.description"),
		},
	};
};

const OrganizationContactImportPage = () => {
	const { t } = useTranslation();
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>(undefined);
	const { addFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.contact.action.import_from_csv.title"));
	}, []);

	const onFetchCustomFields = useCallback(async () => {
		const params: Record<string, string> = {
			resource_name: "CONTACT",
			custom_field_resource_type: "CONTACT",
			custom_field_permission_scope: "WRITE",
		};
		try {
			const res = await api.organization().getResourceTypes(params);
			setCustomFieldsConfig(res.custom_fields);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	}, [addFlash]);

	useEffect(() => {
		onFetchCustomFields();
	}, [onFetchCustomFields]);

	const comparationCheck = (data: ContactApi, fieldName: string) => {
		if (!data) return false;
		switch (fieldName) {
			case "description":
				return data.comment;
			case "contact_phone_nr":
				return data.contact_details?.phone_number;
			case "contact_email":
				return data.contact_details?.email;
			case "address_building_nr":
				return data.address?.build_nr;
			case "address_street":
			case "address_zip_code":
			case "address_city":
			case "address_country":
			case "address_flat_nr":
				if (data.address)
					return data.address[
						fieldName.slice(fieldName.indexOf("_") + 1, fieldName.length) as keyof AddressApi
					];
				return "";
			default:
				return data[fieldName as keyof ContactApi];
		}
	};

	const renderComparedValue = (data: ContactApi, fieldName: string, differs?: boolean): JSX.Element => {
		if (!data) return <span></span>;
		let caption, title;

		const replacedCf = fieldName.replace("+", "");
		const cf = data?.custom_fields?.find(
			(x: CustomFieldEntityApi) => x.slug === camelToSnakeCase(replacedCf, true)
		);
		if (cf && Object.keys(cf).length > 0) {
			title = cf.value;
			caption = title;
		} else {
			switch (fieldName) {
				case "description":
					title = data.comment;
					caption = title;
					break;
				case "contact_phone_nr":
					title = data.contact_details?.phone_number;
					caption = title;
					break;
				case "contact_email":
					title = data.contact_details?.email;
					caption = title;
					break;
				case "address_building_nr":
					title = data.address?.build_nr;
					caption = title;
					break;
				case "address_street":
				case "address_zip_code":
				case "address_city":
				case "address_country":
				case "address_flat_nr":
					if (data.address) {
						title =
							data.address[
								fieldName.slice(fieldName.indexOf("_") + 1, fieldName.length) as keyof AddressApi
							];
						caption = title;
					} else {
						title = "";
						caption = "";
					}
					break;
				default:
					title = data[fieldName as keyof ContactApi]?.toString();
					caption = title;
			}
		}
		return (
			<span style={{ textDecoration: `${differs ? "line-through" : "none"}` }} title={title}>
				{caption}
			</span>
		);
	};

	if (!customFieldsConfig) return <LoadingContainer />;

	return (
		<>
			<Header title={t("modules.contact.action.import_from_csv.title")} />
			<ImportCsv
				resourceType="contacts"
				customFieldsConfig={customFieldsConfig}
				redirectionUrl={`/${organization.id}/contacts`}
				importCsvRequestParams="contact_group,custom_fields,cards"
				comparationCheck={comparationCheck}
				renderComparedValue={renderComparedValue}
				translations={getTranslationsConfig(t)}
				workingInBackground
			/>
		</>
	);
};
export default OrganizationContactImportPage;
